import * as React from 'react';
import type { GrowthPlan } from '@shared/api-client/growth';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Alert } from '@/components/ui/alert';

export const textAreaClass =
  'w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-60';

export const PlanForm = ({
  plan,
  canManage,
  saving,
  error,
  onSave,
  onCancel,
}: {
  plan: GrowthPlan;
  canManage: boolean;
  saving: boolean;
  error?: string;
  onSave: (plan: GrowthPlan) => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = React.useState(plan);
  const [competitors, setCompetitors] = React.useState(
    plan.competitors.join('\n'),
  );
  const [topics, setTopics] = React.useState(plan.topics.join('\n'));
  const lines = (value: string) =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  return (
    <form
      className="space-y-5 rounded-xl border border-border bg-surface p-5 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          ...draft,
          competitors: lines(competitors),
          topics: lines(topics),
        });
      }}
    >
      <div>
        <h2 className="font-semibold text-ink">
          What should GainSignal look for?
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Research starts with your product profile. Refine the focus whenever
          your priorities change.
        </p>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <Field label="Your growth goal" htmlFor="growth-goal">
        <textarea
          className={textAreaClass}
          rows={2}
          required
          minLength={10}
          maxLength={600}
          value={draft.goal}
          disabled={!canManage || saving}
          onChange={(event) => setDraft({ ...draft, goal: event.target.value })}
        />
      </Field>
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Competitor pages"
          htmlFor="growth-competitors"
          hint="Up to five URLs, one per line. Use a blog, pricing page, or changelog for focused monitoring. Leave empty for automatic discovery."
        >
          <textarea
            className={textAreaClass}
            rows={5}
            maxLength={10240}
            value={competitors}
            disabled={!canManage || saving}
            onChange={(event) => setCompetitors(event.target.value)}
            placeholder="https://competitor.com/blog"
          />
        </Field>
        <Field
          label="Customer problems and topics"
          htmlFor="growth-topics"
          hint="Up to five topics, one per line. Leave empty to research from your product profile."
        >
          <textarea
            className={textAreaClass}
            rows={5}
            maxLength={650}
            value={topics}
            disabled={!canManage || saving}
            onChange={(event) => setTopics(event.target.value)}
            placeholder="Questions your customers are trying to answer"
          />
        </Field>
      </div>
      <label className="flex items-center gap-3 text-sm text-ink">
        <input
          type="checkbox"
          className="size-4 accent-accent"
          checked={draft.enabled}
          disabled={!canManage || saving}
          onChange={(event) =>
            setDraft({ ...draft, enabled: event.target.checked })
          }
        />
        Keep researching this project every day
      </label>
      <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Close
        </Button>
        {canManage && (
          <Button type="submit" loading={saving}>
            Save research focus
          </Button>
        )}
      </div>
    </form>
  );
};
